import {
  Accordion,
  Alert,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBrandApple,
  IconBrandGooglePlay,
  IconChartLine,
  IconCheck,
  IconChecklist,
  IconClock,
  IconCoin,
  IconDeviceMobile,
  IconFlame,
  IconGift,
  IconQuote,
  IconRocket,
  IconShield,
  IconStar,
  IconTarget,
  IconTrendingUp,
  IconTrophy,
  IconUser,
  IconUsers,
  IconWallet,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import Footer from "../../components/Footer";
import { PerformRequest } from "../../hooks/usePerformRequest";
import { useScrollReveal, useStaggerReveal } from "../../hooks/useScrollReveal";
import { AppName } from "../../lib/data";
import { Endpoints } from "../../lib/endpoints";
import "./styles.scss";

/* ──────────────────────── Animated Counter ──────────────────────── */
function AnimatedCounter({
  end,
  prefix = "",
  suffix = "",
  duration = 2000,
  start,
}: {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  start: boolean;
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number;
    let raf: number;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.floor(eased * end));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [start, end, duration]);

  return (
    <span>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

/* ──────────────────────── Homepage ──────────────────────── */
const Homepage = () => {
  const [howItWorksRef, howItWorksVisible] = useScrollReveal();
  const [featuresRef, featuresVisible] = useStaggerReveal();
  const [numbersRef, numbersVisible] = useScrollReveal();
  const [testimonialsRef, testimonialsVisible] = useStaggerReveal();
  const [downloadRef, downloadVisible] = useScrollReveal();
  const [faqRef, faqVisible] = useScrollReveal();
  const [waitlistRef, waitlistVisible] = useScrollReveal();

  const [waitlistFirstName, setWaitlistFirstName] = useState("");
  const [waitlistLastName, setWaitlistLastName] = useState("");
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistPhone, setWaitlistPhone] = useState("");
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistSuccessMessage, setWaitlistSuccessMessage] = useState("");
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  const isValidNigerianPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("234") && cleaned.length === 13) return true;
    if (cleaned.startsWith("0") && cleaned.length === 11) return true;
    if (cleaned.length === 10 && !cleaned.startsWith("0")) return true;
    return false;
  };

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistError(null);
    setWaitlistSuccess(false);
    setWaitlistSuccessMessage("");

    if (
      !waitlistFirstName.trim() ||
      !waitlistLastName.trim() ||
      !waitlistEmail.trim() ||
      !waitlistPhone.trim()
    ) {
      setWaitlistError("Please fill in all fields.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistEmail.trim())) {
      setWaitlistError("Please enter a valid email address.");
      return;
    }

    if (!isValidNigerianPhone(waitlistPhone.trim())) {
      setWaitlistError(
        "Please enter a valid Nigerian phone number (e.g. 08012345678)."
      );
      return;
    }

    setWaitlistLoading(true);
    const endpoint = Endpoints.JoinWaitlist();
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        firstName: waitlistFirstName.trim(),
        lastName: waitlistLastName.trim(),
        email: waitlistEmail.trim().toLowerCase(),
        phone: waitlistPhone.trim(),
        source: "website",
      },
    });
    setWaitlistLoading(false);

    if (r.status === 200 || r.status === 201) {
      setWaitlistSuccess(true);
      setWaitlistSuccessMessage(
        "You're on the waitlist. We'll email you when your access is ready — no need to do anything else for now."
      );
      setWaitlistFirstName("");
      setWaitlistLastName("");
      setWaitlistEmail("");
      setWaitlistPhone("");
    } else if (r.status === 409) {
      setWaitlistError(
        r.message ||
          "You're already on the waitlist, or an account with these details already exists."
      );
    } else {
      setWaitlistError(r.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="homepage">
      {/* ════════════════════════ HERO ════════════════════════ */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="grid-pattern"></div>
        </div>

        <Container size="xl" className="hero-container">
          <div className="hero-split">
            {/* Left — Copy */}
            <div className="hero-copy">
              <div className="hero-badge">
                <span style={{ textTransform: "uppercase" }}>
                  <Text size="sm" className="text-slate-600 font-semibold ">
                    Made with ❤️ for Nigerians 🇳🇬
                  </Text>
                </span>
              </div>

              <Title order={1} className="hero-title">
                <span style={{ color: "#1e293b" }}>Your Lifestyle.</span>
                <span className="gradient-text">Verified. Rewarded.</span>
              </Title>

              <Text size="xl" className="hero-description">
                Turn your daily activities into momentum, status, and real
                rewards. Join the verified participation network that pays you
                to be you.
              </Text>

              <Group gap="md" className="hero-cta">
                <a
                  href="https://apps.apple.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="xl"
                    className="cta-primary"
                    leftSection={<IconBrandApple size={24} />}
                  >
                    App Store
                  </Button>
                </a>
                <a
                  href="https://play.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="xl"
                    className="cta-secondary"
                    leftSection={<IconBrandGooglePlay size={24} />}
                  >
                    Google Play
                  </Button>
                </a>
              </Group>

              <Group gap="xl" className="hero-stats">
                <div className="stat-item">
                  <div className="stat-icon">
                    <IconShield size={28} />
                  </div>
                  <div className="stat-content">
                    <Text className="stat-value">Verified</Text>
                    <Text size="sm" c="dimmed">
                      Actions
                    </Text>
                  </div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <IconTrophy size={28} />
                  </div>
                  <div className="stat-content">
                    <Text className="stat-value">Rank</Text>
                    <Text size="sm" c="dimmed">
                      Up
                    </Text>
                  </div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <IconWallet size={28} />
                  </div>
                  <div className="stat-content">
                    <Text className="stat-value">Earn</Text>
                    <Text size="sm" c="dimmed">
                      Daily
                    </Text>
                  </div>
                </div>
              </Group>
            </div>

            {/* Right — Phone Mockup (CSS-only) */}
            <div className="hero-phone-wrap">
              <div className="phone-glow"></div>
              <div className="phone-frame">
                <div className="phone-notch"></div>
                <div className="phone-screen">
                  <div className="mock-header">
                    <span className="mock-greeting">Hey, Adebayo! 👋</span>
                    <div className="mock-bell">🔔</div>
                  </div>
                  <div className="mock-balance-card">
                    <span className="mock-balance-label">Your Earnings</span>
                    <span className="mock-balance-value">₦12,450</span>
                    <span className="mock-balance-sub">+₦2,800 this week</span>
                  </div>
                  <div className="mock-section-title">Active Opportunities</div>
                  <div className="mock-mission">
                    <div
                      className="mock-mission-icon"
                      style={{ background: "rgba(16,185,129,0.15)" }}
                    >
                      📸
                    </div>
                    <div className="mock-mission-info">
                      <span className="mock-mission-name">Share a Photo</span>
                      <span className="mock-mission-reward">+₦500</span>
                    </div>
                    <div className="mock-mission-badge">NEW</div>
                  </div>
                  <div className="mock-mission">
                    <div
                      className="mock-mission-icon"
                      style={{ background: "rgba(251,191,36,0.15)" }}
                    >
                      🎧
                    </div>
                    <div className="mock-mission-info">
                      <span className="mock-mission-name">Listen & Review</span>
                      <span className="mock-mission-reward">+₦300</span>
                    </div>
                    <div className="mock-mission-check">✓</div>
                  </div>
                  <div className="mock-mission">
                    <div
                      className="mock-mission-icon"
                      style={{ background: "rgba(139,92,246,0.15)" }}
                    >
                      📢
                    </div>
                    <div className="mock-mission-info">
                      <span className="mock-mission-name">Social Share</span>
                      <span className="mock-mission-reward">+₦750</span>
                    </div>
                    <div className="mock-mission-badge hot">HOT</div>
                  </div>
                  <div className="mock-streak">
                    <IconFlame size={16} color="#f59e0b" />
                    <span>7−day streak!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ════════════════════════ HOW IT WORKS ════════════════════════ */}
      <section
        className="how-it-works-section"
        id="how-it-works"
        ref={howItWorksRef}
      >
        <div className="how-it-works-background">
          <div className="how-it-works-orb orb-1"></div>
          <div className="how-it-works-orb orb-2"></div>
          <div className="how-it-works-orb orb-3"></div>
        </div>

        <Container
          size="xl"
          className={`how-it-works-container reveal-container ${howItWorksVisible ? "revealed" : ""}`}
        >
          <Stack gap="xl" align="center" className="how-it-works-content">
            <div className="how-it-works-header">
              <Title order={2} className="how-it-works-title">
                How It <span className="gradient-text">Works</span>
              </Title>
              <Text size="lg" className="how-it-works-description">
                Simple steps to turn verified actions into value.
              </Text>
            </div>

            <div className="steps-container">
              <div className="step-card reveal-item" data-step="1">
                <div className="step-number">01</div>
                <div className="step-icon-wrapper">
                  <IconChecklist size={32} />
                </div>
                <div className="step-content">
                  <Title order={3} className="step-title">
                    Complete Activities
                  </Title>
                  <Text className="step-description">
                    Choose from a feed of live activities. Follow the
                    instructions and verify your action with a photo or
                    check-in.
                  </Text>
                </div>
              </div>

              <div className="step-connector">
                <IconArrowRight size={32} />
              </div>

              <div className="step-card reveal-item" data-step="2">
                <div className="step-number">02</div>
                <div className="step-icon-wrapper">
                  <IconChartLine size={40} />
                </div>
                <div className="step-content">
                  <Title order={3} className="step-title">
                    Build Momentum
                  </Title>
                  <Text className="step-description">
                    Consistency is key. Streak counting and XP growth unlock
                    higher tiers and exclusive opportunities.
                  </Text>
                </div>
              </div>

              <div className="step-connector">
                <IconArrowRight size={32} />
              </div>

              <div className="step-card reveal-item" data-step="3">
                <div className="step-number">03</div>
                <div className="step-icon-wrapper">
                  <IconWallet size={32} />
                </div>
                <div className="step-content">
                  <Title order={3} className="step-title">
                    Get Paid
                  </Title>
                  <Text className="step-description">
                    Every verified action earns you Coins. Withdraw instantly to
                    your bank account or save for bigger goals.
                  </Text>
                </div>
              </div>
            </div>
          </Stack>
        </Container>
      </section>

      {/* ════════════════════════ FEATURES ════════════════════════ */}
      <section className="features-section" id="features" ref={featuresRef}>
        <div className="features-background">
          <div className="features-orb orb-1"></div>
          <div className="features-orb orb-2"></div>
        </div>

        <Container
          size="xl"
          className={`features-container reveal-container ${featuresVisible ? "revealed" : ""}`}
        >
          <Stack gap="xl" align="center" className="features-content">
            <div className="features-header">
              <Title order={2} className="features-title">
                Inside <span className="gradient-text">{AppName}</span>
              </Title>
              <Text size="lg" className="features-description">
                Built for Verified Participation. Designed for You.
              </Text>
            </div>

            <div className="features-grid">
              {[
                {
                  icon: <IconRocket size={32} />,
                  title: "Instant Verification",
                  desc: "Our advanced verification system approves your actions in seconds, so you get paid faster.",
                },
                {
                  icon: <IconTrendingUp size={32} />,
                  title: "Live Leaderboards",
                  desc: "Compete with friends and the global community. Top performers earn bonus rewards every week.",
                },
                {
                  icon: <IconShield size={32} />,
                  title: "Bank-Grade Security",
                  desc: "Your data and earnings are encrypted and protected. Withdrawal channels are secure and reliable.",
                },
                {
                  icon: <IconClock size={32} />,
                  title: "Daily Drops",
                  desc: "New activities drop every day. Turn your notifications on so you never miss an earning opportunity.",
                },
                {
                  icon: <IconGift size={32} />,
                  title: "Badges & Status",
                  desc: "Unlock rare badges as you complete milestones. Show off your status on your profile.",
                },
                {
                  icon: <IconUsers size={32} />,
                  title: "Community Pools",
                  desc: "Join forces with others to complete group challenges and split massive reward pools.",
                },
              ].map((feature, i) => (
                <div
                  key={feature.title}
                  className="feature-card reveal-item"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="feature-icon-wrapper">{feature.icon}</div>
                  <Title order={3} className="feature-title">
                    {feature.title}
                  </Title>
                  <Text className="feature-description">{feature.desc}</Text>
                </div>
              ))}
            </div>
          </Stack>
        </Container>
      </section>

      {/* ════════════════════════ NUMBERS (NEW) ════════════════════════ */}
      <section className="numbers-section" ref={numbersRef}>
        <div className="numbers-background">
          <div className="numbers-orb orb-1"></div>
          <div className="numbers-orb orb-2"></div>
        </div>

        <Container
          size="xl"
          className={`numbers-container reveal-container ${numbersVisible ? "revealed" : ""}`}
        >
          <div className="numbers-header">
            <Title order={2} className="numbers-title">
              The <span className="numbers-gradient-text">Movement</span> Is
              Growing
            </Title>
            <Text className="numbers-subtitle">
              Real people. Real activities. Real rewards.
            </Text>
          </div>

          <div className="numbers-grid">
            <div className="number-card reveal-item">
              <div className="number-icon">
                <IconUsers size={28} />
              </div>
              <div className="number-value">
                <AnimatedCounter
                  end={10000}
                  suffix="+"
                  start={numbersVisible}
                />
              </div>
              <div className="number-label">Active Users</div>
            </div>
            <div
              className="number-card reveal-item"
              style={{ transitionDelay: "150ms" }}
            >
              <div className="number-icon">
                <IconTarget size={28} />
              </div>
              <div className="number-value">
                <AnimatedCounter
                  end={50000}
                  suffix="+"
                  start={numbersVisible}
                />
              </div>
              <div className="number-label">Activities Completed</div>
            </div>
            <div
              className="number-card reveal-item"
              style={{ transitionDelay: "300ms" }}
            >
              <div className="number-icon">
                <IconCoin size={28} />
              </div>
              <div className="number-value">
                <AnimatedCounter
                  end={2}
                  prefix="₦"
                  suffix="M+"
                  start={numbersVisible}
                  duration={1500}
                />
              </div>
              <div className="number-label">Paid Out</div>
            </div>
            <div
              className="number-card reveal-item"
              style={{ transitionDelay: "450ms" }}
            >
              <div className="number-icon">
                <IconFlame size={28} />
              </div>
              <div className="number-value">
                <AnimatedCounter
                  end={30}
                  suffix="-day"
                  start={numbersVisible}
                  duration={1200}
                />
              </div>
              <div className="number-label">Longest Streak</div>
            </div>
          </div>
        </Container>
      </section>

      {/* ════════════════════════ TESTIMONIALS ════════════════════════ */}
      <section className="testimonials-section" ref={testimonialsRef}>
        <div className="testimonials-background">
          <div className="testimonials-orb orb-1"></div>
          <div className="testimonials-orb orb-2"></div>
        </div>

        <Container
          size="xl"
          className={`testimonials-container reveal-container ${testimonialsVisible ? "revealed" : ""}`}
        >
          <Stack gap="xl" align="center" className="testimonials-content">
            <div className="testimonials-header">
              <Title order={2} className="testimonials-title">
                What Users Are <span className="gradient-text">Saying</span>
              </Title>
              <Text size="lg" className="testimonials-description">
                Join thousands of users converting their lifestyle into
                currency.
              </Text>
            </div>

            <div className="testimonials-grid">
              {[
                {
                  text: `"Finally, an app that actually pays me for doing the things I already love. The verification is super fast."`,
                  name: "Adebayo O.",
                  role: "Level 5 Achiever",
                },
                {
                  text: `"I've hit a 30-day streak and the bonus rewards are insane. It's addictive in the best way possible."`,
                  name: "Chioma K.",
                  role: "Streak Master",
                },
                {
                  text: `"Withdrawals are instant. I use this to cover my data and subscriptions every month. Highly recommend."`,
                  name: "Emeka T.",
                  role: "Top Earner",
                },
              ].map((t, i) => (
                <div
                  key={t.name}
                  className="testimonial-card reveal-item"
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div className="testimonial-glow"></div>
                  <div className="testimonial-quote-icon">
                    <IconQuote size={24} />
                  </div>
                  <Text className="testimonial-text">{t.text}</Text>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                      <IconUser size={24} />
                    </div>
                    <div className="testimonial-author-info">
                      <Text className="testimonial-name">{t.name}</Text>
                      <Text className="testimonial-role">{t.role}</Text>
                      <div className="testimonial-rating">
                        {[...Array(5)].map((_, i) => (
                          <IconStar
                            key={i}
                            size={14}
                            fill="currentColor"
                            className="star-filled"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Stack>
        </Container>
      </section>

      {/* ════════════════════════ DOWNLOAD CTA ════════════════════════ */}
      <section className="download-section" id="download" ref={downloadRef}>
        <div className="download-background">
          <div className="download-orb orb-1"></div>
          <div className="download-orb orb-2"></div>
          <div className="download-particles">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>
        </div>

        <Container
          size="md"
          className={`download-container reveal-container ${downloadVisible ? "revealed" : ""}`}
        >
          <Stack gap="xl" align="center" className="download-content">
            <div className="download-badge">
              <IconDeviceMobile size={18} />
              <span>Available on iOS & Android</span>
            </div>

            <div className="download-header">
              <Title order={2} className="download-title">
                Get <span className="gradient-text">{AppName}</span> Today
              </Title>
              <Text size="lg" className="download-description">
                Start earning in minutes. Your verified lifestyle is waiting.
              </Text>
            </div>

            <Group gap="md" className="hero-cta">
              <a
                href="https://apps.apple.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="xl"
                  className="cta-primary"
                  leftSection={<IconBrandApple size={24} />}
                >
                  App Store
                </Button>
              </a>
              <a
                href="https://play.google.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="xl"
                  className="cta-secondary"
                  leftSection={<IconBrandGooglePlay size={24} />}
                >
                  Google Play
                </Button>
              </a>
            </Group>

            <div className="download-flag">
              <span>🇳🇬</span> Currently available only in Nigeria
            </div>
          </Stack>
        </Container>
      </section>

      {/* ════════════════════════ WAITLIST ════════════════════════ */}
      <section className="waitlist-section" id="waitlist" ref={waitlistRef}>
        <div className="waitlist-background">
          <div className="waitlist-orb orb-1"></div>
          <div className="waitlist-orb orb-2"></div>
        </div>

        <Container
          size="sm"
          className={`waitlist-container reveal-container ${waitlistVisible ? "revealed" : ""}`}
        >
          <Paper
            className="waitlist-card reveal-item"
            radius="xl"
            p={40}
            withBorder
            shadow="xl"
          >
            <Stack gap="xl" align="center">
              <div className="waitlist-header" style={{ textAlign: "center" }}>
                <Title order={2} className="waitlist-title">
                  Get Early <span className="gradient-text">Access</span>
                </Title>
                <Text size="lg" c="dimmed" mt="xs">
                  We are launching in phases. Secure your spot to get early
                  access and exclusive rewards.
                </Text>
              </div>

              {waitlistSuccess ? (
                <Alert
                  color="green"
                  title="Success!"
                  variant="light"
                  icon={<IconCheck size={18} />}
                  radius="md"
                  style={{ width: "100%" }}
                >
                  {waitlistSuccessMessage}
                </Alert>
              ) : (
                <form onSubmit={handleJoinWaitlist} style={{ width: "100%" }}>
                  <Stack gap="md">
                    {waitlistError && (
                      <Alert
                        color="red"
                        variant="light"
                        icon={<IconX size={18} />}
                        radius="md"
                      >
                        {waitlistError}
                      </Alert>
                    )}
                    <Group grow>
                      <TextInput
                        placeholder="First Name"
                        label="First Name"
                        required
                        size="md"
                        radius="md"
                        value={waitlistFirstName}
                        onChange={(e) =>
                          setWaitlistFirstName(e.currentTarget.value)
                        }
                      />
                      <TextInput
                        placeholder="Last Name"
                        label="Last Name"
                        required
                        size="md"
                        radius="md"
                        value={waitlistLastName}
                        onChange={(e) =>
                          setWaitlistLastName(e.currentTarget.value)
                        }
                      />
                    </Group>
                    <TextInput
                      placeholder="email@example.com"
                      label="Email Address"
                      type="email"
                      required
                      size="md"
                      radius="md"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.currentTarget.value)}
                    />
                    <TextInput
                      placeholder="08012345678"
                      label="Phone Number"
                      description="Nigerian mobile number"
                      required
                      size="md"
                      radius="md"
                      value={waitlistPhone}
                      onChange={(e) => setWaitlistPhone(e.currentTarget.value)}
                    />
                    <Button
                      type="submit"
                      size="lg"
                      radius="md"
                      fullWidth
                      className="waitlist-submit"
                      loading={waitlistLoading}
                      mt="md"
                    >
                      Join Early Access
                    </Button>
                  </Stack>
                </form>
              )}
            </Stack>
          </Paper>
        </Container>
      </section>

      {/* ════════════════════════ FAQ ════════════════════════ */}
      <section className="faq-section" id="faq" ref={faqRef}>
        <div className="faq-background">
          <div className="faq-orb orb-1"></div>
          <div className="faq-orb orb-2"></div>
        </div>

        <Container
          size="md"
          className={`faq-container reveal-container ${faqVisible ? "revealed" : ""}`}
        >
          <Stack gap="xl" align="center" className="faq-content">
            <div className="faq-header">
              <Title order={2} className="faq-title">
                Frequently Asked{" "}
                <span className="gradient-text">Questions</span>
              </Title>
              <Text size="lg" className="faq-description">
                Everything you need to know about {AppName}
              </Text>
            </div>

            <Accordion
              variant="separated"
              radius="md"
              className="faq-accordion"
              chevronPosition="right"
            >
              <Accordion.Item value="what-is-virel" className="faq-item">
                <Accordion.Control className="faq-control">
                  What is {AppName}?
                </Accordion.Control>
                <Accordion.Panel className="faq-panel">
                  {AppName} is a participation-powered lifestyle platform where
                  verified digital actions turn into momentum and rewards. You
                  complete activities, we verify them, you get paid.
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="how-to-earn" className="faq-item">
                <Accordion.Control className="faq-control">
                  How do I earn rewards?
                </Accordion.Control>
                <Accordion.Panel className="faq-panel">
                  Browse the feed for activities. Each activity has instructions
                  (e.g., share a post, take a photo). Submit your proof, and
                  once verified, Coins are added to your wallet.
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="cashout-process" className="faq-item">
                <Accordion.Control className="faq-control">
                  How do I withdraw my money?
                </Accordion.Control>
                <Accordion.Panel className="faq-panel">
                  Go to your Wallet tap, click Withdraw, enter your bank
                  details, and cash out. Payments are processed instantly to all
                  major banks.
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="leaderboard" className="faq-item">
                <Accordion.Control className="faq-control">
                  What are leaderboards?
                </Accordion.Control>
                <Accordion.Panel className="faq-panel">
                  Leaderboards rank users based on their activity and earnings.
                  Top ranked users often qualify for exclusive campaigns and big
                  bonus drops.
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value="free-to-use" className="faq-item">
                <Accordion.Control className="faq-control">
                  Is {AppName} free?
                </Accordion.Control>
                <Accordion.Panel className="faq-panel">
                  Yes, {AppName} is 100% free to download and use. We pay you,
                  not the other way around.
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Stack>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Homepage;
